import { useState, useCallback } from "react";
import { defaultCaseCategories, type CaseCategory } from "@/data/caseArticleCategories";

const STORAGE_KEY = "minzo-case-categories";

function load(): CaseCategory[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return defaultCaseCategories.map((c) => ({ ...c }));
}

function save(cats: CaseCategory[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cats));
}

export function useEditableCaseCategories() {
  const [categories, setCategories] = useState<CaseCategory[]>(load);

  const updateCategory = useCallback((id: string, newName: string) => {
    setCategories((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, name: newName } : c));
      save(next);
      return next;
    });
  }, []);

  const addCategory = useCallback((name: string) => {
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    if (!id) return;
    setCategories((prev) => {
      const next = [...prev, { id, name }];
      save(next);
      return next;
    });
  }, []);

  const removeCategory = useCallback((id: string) => {
    setCategories((prev) => {
      const next = prev.filter((c) => c.id !== id);
      save(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCategories(defaultCaseCategories.map((c) => ({ ...c })));
  }, []);

  const getCategoryName = useCallback(
    (id: string) => categories.find((c) => c.id === id)?.name || id,
    [categories]
  );

  return {
    categories,
    updateCategory,
    addCategory,
    removeCategory,
    resetToDefaults,
    getCategoryName,
  };
}
